import React, { ReactElement, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, Typography } from '@mui/material'
import { v4 as uuidv4 } from 'uuid'

import { apiZodios } from '~api/apiZodios'
import { Button, DeleteButton, SaveCancelButtons } from '~components/common'
import Table, { TableHeader } from '~components/common/Table'
import { lightTheme } from '~createTheme'
import { RootState } from '~redux/rootReducer'
import { Attribution, TargetId } from '~types'

import AttributionEntryBox from './AttributionEntryBox'

interface AttributionFlattened {
  attributionId: string
  attributeId: string
  name: string
  value: string
}

type AttributionSettingsTableProps = {
  targetId: TargetId
  onSave?: () => void
  inputAttributions?: Attribution[]
}

const AttributionSettingsTable = ({
  targetId,
  onSave,
  inputAttributions,
}: AttributionSettingsTableProps): ReactElement => {
  const user = useSelector((state: RootState) => state?.auth.user)
  const organizationIdentityId = useSelector(
    (state: RootState) => state?.organization.selectedOrganization?.id,
  )
  const [attributions, setAttributions] = useState<Attribution[]>(inputAttributions || [])
  const [attributionsFlattened, setAttributionsFlattened] = useState<AttributionFlattened[]>([])
  const [showAttributionInput, setShowAttributionInput] = useState(false)
  const [attributionNameInput, setAttributionNameInput] = useState('')
  const [attributionValueInput, setAttributionValueInput] = useState('')
  const [isValid, setIsValid] = useState(false)

  useEffect(() => {
    setAttributionsFlattened(
      attributions.map((a) => ({
        attributionId: a.id || '',
        attributeId: a.attribute.id || '',
        name: a.attribute.name,
        value: a.value,
      })),
    )
  }, [attributions])

  const listAttributions = async (): Promise<void> => {
    try {
      if (!user) return
      const response = await apiZodios.core_listAttributions({
        user_id: user.id,
        target_ids: [targetId],
      })
      setAttributions(response.attributions.map((a) => ({ ...a, target: targetId })))
    } catch (_err) {}
  }

  const addAttribution = async (): Promise<void> => {
    try {
      if (!user || !organizationIdentityId) return
      const optimisticId = uuidv4()
      const optimisticAttribution: Attribution = {
        id: optimisticId,
        target: targetId,
        attribute: {
          code: '',
          name: attributionNameInput,
          owner_id: '',
          info: '',
          id: optimisticId,
        },
        value: attributionValueInput,
        attribute_id: optimisticId,
        name: attributionNameInput,
      }
      setAttributions((prev) => [...prev, optimisticAttribution])
      setShowAttributionInput(false)
      setAttributionNameInput('')
      setAttributionValueInput('')

      await apiZodios.core_createAttribution({
        user_id: user.id,
        target_id: targetId,
        organization_identity_id: organizationIdentityId,
        name: attributionNameInput,
        value: attributionValueInput,
      })

      listAttributions()
    } catch (_err) {
      listAttributions()
    }
  }

  const removeAttribution = async (attribution: AttributionFlattened): Promise<void> => {
    try {
      if (!user) return
      setAttributions((prev) => prev.filter((a) => a.id !== attribution.attributionId))

      await apiZodios.core_unattribute({
        user_id: user.id,
        attribution_id: attribution.attributionId,
      })
      listAttributions()
    } catch (_err) {
      listAttributions()
    }
  }

  const handleAttributionChange = (id: string, field: 'name' | 'value', value: string): void => {
    const newName = field === 'name' ? value : attributionNameInput
    const newValue = field === 'value' ? value : attributionValueInput

    if (field === 'name') {
      setAttributionNameInput(value)
    } else {
      setAttributionValueInput(value)
    }

    setIsValid(newName.trim() !== '' && newValue.trim() !== '')
  }

  const cancelAddAttribution = (): void => {
    setShowAttributionInput(false)
    setAttributionNameInput('')
    setAttributionValueInput('')
  }

  useEffect(() => {
    listAttributions()
  }, [targetId, user])

  const AttributionRowContent: React.FC<{ row: AttributionFlattened }> = ({ row }) => (
    <Box display='flex' alignItems='center' justifyContent='space-between' width='100%' mb={1}>
      <Box display='flex' gap={2}>
        <Typography variant='h4'>{row.name}:</Typography>
        <Typography variant='h4'>{row.value}</Typography>
      </Box>
      <DeleteButton item={row} onDelete={removeAttribution} itemName={row.name} />
    </Box>
  )

  const attributionTable: TableHeader<AttributionFlattened>[] = [
    {
      label: 'Additional Data',
      render: (row) => <AttributionRowContent row={row} />,
    },
  ]

  return (
    <Box display='flex' flexDirection='column' width='600px' padding={2}>
      <Table
        headers={attributionTable}
        data={attributionsFlattened}
        loading={false}
        ignoreMaxHeight={true}
      />
      <Box display='flex' flexDirection='column' width='100%' mt={2}>
        {showAttributionInput ? (
          <>
            <Box display='flex' justifyContent='center' width='100%' mb={2}>
              <Box width='100%' maxWidth='400px'>
                <AttributionEntryBox
                  id='new'
                  name={attributionNameInput}
                  value={attributionValueInput}
                  onChange={handleAttributionChange}
                />
              </Box>
            </Box>
            <Box display='flex' justifyContent='flex-end'>
              <SaveCancelButtons
                onSave={addAttribution}
                onCancel={cancelAddAttribution}
                isSaveDisabled={!isValid}
              />
            </Box>
          </>
        ) : (
          <Box display='flex' justifyContent='flex-end'>
            <Button
              variant='outlined'
              color='secondary'
              sx={{ m: 2 }}
              onClick={() => setShowAttributionInput(true)}
            >
              Add Additional Data
            </Button>
            {onSave && (
              <Button
                variant='contained'
                color='secondary'
                style={{ margin: lightTheme.spacing(2) }}
                onClick={onSave}
              >
                Save
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default AttributionSettingsTable
